import React from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import makeAnimated from 'react-select/animated'
import * as _ from 'lodash'

import { useCreateCall } from '../../lib/api/create'
import { useGetQuery } from '../../lib/api/get'

import closeBtn from '../../assets/images/closeBtn.png'
const animatedComponents = makeAnimated()

/**
 * Renders a modal for creating and sending a new message. The modal includes
 * a form with fields for subject and message content. It also provides a submit
 * button that triggers the message send action.
 *
 * @module messages/CreateChat
 * @description Renders a modal for creating and sending a new message. The modal includes
 * a form with fields for subject and message content. It also provides a submit
 * button that triggers the message send action.
 * @example
 * const setNewMessageModal = () => {};
 * const handleSubmit = () => {};
 * const sendMessage = () => {};
 * const register = () => {};
 * const createCallMutation = { isLoading: false };
 *
 * return (
 *   <MessageModal
 *     setNewMessageModal={setNewMessageModal}
 *     handleSubmit={handleSubmit}
 *     sendMessage={sendMessage}
 *     register={register}
 *     createCallMutation={createCallMutation}
 *   />
 * );
 *
 * @param {function} setNewMessageModal - Function to toggle the visibility of the modal.
 * @param {function} handleSubmit - Function from react-hook-form to handle form submission.
 * @param {function} sendMessage - Function to handle the sending of the message.
 * @param {function} register - Function from react-hook-form to register input fields.
 * @param {Object} createCallMutation - Object containing the mutation state for the message send request.
 * @param {boolean} createCallMutation.isLoading - Boolean indicating if the send message request is loading.
 * @returns {React.Element} The rendered message modal component.
 */
export const CreateChat = ({
  isLocalEnvironment,
  setNewMessageModal,
  isGroupChat,
  authDetails,
}) => {
  const createCallMutation = useCreateCall()
  const [multiValue, setMultiValue] = React.useState([])
  const [switchButton, setSwitchButton] = React.useState(false)
  const [childApplication, setChildApplication] = React.useState(false)
  const dependency = useGetQuery({
    apiURL: isLocalEnvironment + '/getData',
    apiEndpoint: isGroupChat ? 'classRoom' : 'users',
    isDynamic: true,
  })

  if (dependency.isLoading) {
    return <div>Loading...</div>
  }

  let options

  if (isGroupChat) {
    options =
      dependency.data?.formData.map((f) => {
        return { value: f.classRoomId, label: f.classRoomName }
      }) || []
    if (options.length > 0) {
      options.splice(0, 0, { label: 'All Rooms', value: 'select_all' })
    }
  } else {
    options =
      dependency.data?.formData.map((f) => {
        return { value: f.userId, label: `${f.userFullName} (${f.userName})` }
      }) || []
    if (options.length > 0) {
      options.splice(0, 0, { label: 'All Users', value: 'select_all' })
    }
  }

  const handleMultiChange = (option) => {
    setMultiValue(option)
  }

  const submitHandler = async (e) => {
    e.preventDefault()
    const dataForm = new FormData(e.target)
    const data = {
      chatSubject: dataForm.get('subject'),
      message: JSON.stringify({
        message: dataForm.get('message'),
        userName: authDetails.session.user.userFullName,
      }), //dataForm.get('message'),
    }
    if (childApplication) {
      data['applicationId'] = dataForm.get('childApplication')
      if (data['applicationId'] === '') {
        delete data.applicationId
      }
    }
    const selectedIds = []
    if (multiValue.length > 0) {
      multiValue.map((id) => {
        if (id.value === 'select_all') {
          options.map((elem) => elem.value !== 'select_all' && selectedIds.push(elem.value))
        } else {
          selectedIds.push(id.value)
        }
      })
    }

    const ids = _.uniqBy(selectedIds, function (e) {
      return e
    })
    if (isGroupChat) {
      data['classRoomIds'] = ids
      data['isPickupPermissions'] = switchButton
    } else {
      if (
        authDetails.role === 'Staff' ||
        authDetails.role === 'Office Staff' ||
        authDetails.role === 'Staff Admin'
      ) {
        data['userIds'] = selectedIds
      } else {
        data['userIds'] = [authDetails.session.userId]
      }
      data['isGroupChat'] = 0
    }

    await createCallMutation
      .mutateAsync({
        url: isLocalEnvironment + '/addData',
        data: data, //data,
        apiEndpoint: 'chats',
        params: '',
        isDynamic: true,
        messageTitle: 'Chats',
        message: 'Chat created successfully!',
      })
      .then(() => setNewMessageModal(false))
  }

  const urlPath = window.location.pathname
  const urlLink = urlPath.split('/organization/')
  return (
    <div
      className="trtui-modal trtui-fade trtui-show"
      tabIndex={-1}
      style={{ display: 'block', backgroundColor: 'rgba(55,55,55,0.3)' }}
      aria-modal="true"
      role="dialog"
    >
      <div className="trtui-modal-dialog trtui-modal-dialog-centered">
        <div className="trtui-modal-content trtui-border-0 trtui-overflow-hidden">
          <div className="trtui-modal-header trtui-p-3">
            <h4 className="trtui-card-title trtui-mb-0">New Message</h4>
            <button
              type="button"
              className="trtui-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              style={{
                '--vz-btn-close-color': '#000',
                '--vz-btn-close-bg': `url(${closeBtn})`,
                '--vz-btn-close-opacity': '0.5',
                '--vz-btn-close-hover-opacity': '0.75',
                '--vz-btn-close-focus-shadow': '0 0 1 0.25rem rgba(var(--vz-primary-rgb), 0.25)',
                '--vz-btn-close-focus-opacity': 1,
                '--vz-btn-close-disabled-opacity': '0.25',
                '--vz-btn-close-white-filter': 'invert(1) grayscale(100%) brightness(200%)',
                WebkitBoxSizing: 'content-box',
                boxSizing: 'content-box',
                width: '1em',
                height: '1em',
                padding: '0.25em 0.25em',
                color: 'var(--vz-btn-close-color)',
                background: 'transparent var(--vz-btn-close-bg) center/1em auto no-repeat',
                border: 0,
                borderRadius: '0.25rem',
                opacity: 'var(--vz-btn-close-opacity)',
              }}
              onClick={() => setNewMessageModal(false)}
            />
          </div>
          <div className="trtui-modal-body">
            <form className="row" onSubmit={(e) => submitHandler(e)}>
              <div className="trtui-mb-3">
                <label htmlFor="fullName" className="trtui-form-label">
                  Subject
                </label>
                <input
                  type="text"
                  className="trtui-form-control"
                  placeholder="Subject"
                  name="subject"
                />
              </div>
              <div className="trtui-mb-3">
                <label htmlFor="fullName" className="trtui-form-label">
                  Message
                </label>
                <textarea
                  type="text"
                  className="trtui-form-control"
                  placeholder="Message"
                  name="message"
                ></textarea>
              </div>
              {isGroupChat && (
                <div className="trtui-mb-3">
                  <label htmlFor="isPP" className="trtui-form-label">
                    Add Pickup Permissions
                  </label>
                  <label className="toggle-control trtui-mb-5">
                    <input
                      type="checkbox"
                      onClick={() => setSwitchButton(!switchButton)}
                      checked={switchButton}
                      name="isPP"
                      data-testid="inputCheckbox"
                    />
                    <span className="control"></span>
                  </label>
                </div>
              )}
              {authDetails.orgRole === 'AppUser' && (
                <div className="trtui-mb-3">
                  <label htmlFor="emailInput" className="trtui-form-label">
                    Child
                  </label>
                  <select
                    className="trtui-form-control"
                    onChange={() => setChildApplication(true)}
                    name="childApplication"
                  >
                    <option value="">Select Child</option>
                    {authDetails.dependeciesFormAPIS?.['registrations/get/list']?.formData?.map(
                      (appl, ind) => (
                        <option key={ind} value={appl.applicationId}>
                          {appl.studentName}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              )}
              {urlLink.length > 1 &&
                (authDetails.orgRole === 'Staff' ||
                  authDetails.orgRole === 'Office Staff' ||
                  authDetails.orgRole === 'Staff Admin') && (
                  <div className="trtui-mb-3">
                    <label htmlFor="emailInput" className="trtui-form-label">
                      {isGroupChat ? 'Class Room' : 'User'}
                    </label>
                    <Select
                      name="id"
                      options={options}
                      components={animatedComponents}
                      isMulti
                      closeMenuOnSelect={false}
                      onChange={handleMultiChange}
                      menuPlacement="top"
                    />
                  </div>
                )}
              {urlLink.length < 2 &&
                (authDetails.role === 'Staff' ||
                  authDetails.role === 'Office Staff' ||
                  authDetails.role === 'Staff Admin') && (
                  <div className="trtui-mb-3">
                    <label htmlFor="emailInput" className="trtui-form-label">
                      {isGroupChat ? 'Class Room' : 'User'}
                    </label>
                    <Select
                      name="id"
                      options={options}
                      components={animatedComponents}
                      isMulti
                      closeMenuOnSelect={false}
                      onChange={handleMultiChange}
                    />
                  </div>
                )}
              <div className="trtui-text-end">
                <button type="submit" className="trtui-btn trtui-btn-primary">
                  {createCallMutation.isLoading ? (
                    <>
                      <span
                        className="trtui-spinner-border trtui-spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>{' '}
                      Sending...
                    </>
                  ) : (
                    <>Send</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

CreateChat.propTypes = {
  isLocalEnvironment: PropTypes.string,
  setNewMessageModal: PropTypes.func.isRequired,
  envData: PropTypes.object,
  getUsers: PropTypes.bool,
  isGroupChat: PropTypes.bool,
  authDetails: PropTypes.object,
}
